import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Toolbar,
  useMediaQuery,
  useTheme,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Divider,
  Stack,
} from "@mui/material";
import { motion } from "framer-motion";
import HomeHeader from "../../../Components/HomeHeader";
import HomeFooter from "../../../Components/HomeFooter";
import routes from "../../../Config/routes";
import paymentImage from "../../../Components/globalAssets/blackwhite.png";
import marketplaceImage from "../../../Components/globalAssets/logo_placeholder.png";
import frame from "../../../Components/globalAssets/frame.png";
import agencyPreview from "../../../Components/globalAssets/agencyPreview.png";
import laptopFrame from "../../../Components/globalAssets/laptop-02.png";
import marketPreview from "../../../Components/globalAssets/marketplacePreview.png";

const sections = [
  {
    id: 'management',
    title: 'Creator Management Suite',
    content: `Manage, modify, and oversee your agency creators and their campaigns through our powerful portal.`,
    imageFrame: true,
    frameImage: frame,
    previewImage: agencyPreview,
    tag: "Dashboard",
    color: "#4e54c8",
  },
  {
    id: 'blitzPay',
    title: 'BlitzPay – Accounts Payable',
    content: `Streamline payment processes and ensure creators are paid on time through their preferred methods.`,
    imageCircle: true,
    tag: "Payments",
    color: "#00b4d8",
  },
  {
    id: 'marketplace',
    title: 'Your Creators, Everywhere',
    content: `Make your creators visible on the Blitz Marketplace to form partnerships with top brands.`,
    imageLaptop: true,
    frameImage: laptopFrame,
    previewImage: marketPreview,
    tag: "Exposure",
    color: "#ff6b6b",
  },
  {
    id: 'collaborations',
    title: 'Accelerated Creator Collaborations',
    content: `Fast-track partnerships with creators using Blitz's efficient collaboration tools.`,
    imageSrc: marketplaceImage,
    tag: "Partnerships",
    color: "#06d6a0",
  },
];

const fadeIn = { 
  hidden: { opacity: 0, y: 40 }, 
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.6, 
      ease: [0.16, 1, 0.3, 1] 
    }
  } 
};

const cardVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: (i) => ({
    opacity: 1,
    scale: 1,
    transition: {
      delay: i * 0.1,
      duration: 0.5
    }
  })
};

export default function AgenciesModern() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const refs = useRef({});

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1);
      refs.current[id]?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [location.hash]);

  return (
    <Box sx={{ 
      backgroundColor: theme.palette.background.default,
      minHeight: '100vh'
    }}>
      <HomeHeader />
      <Toolbar />

      {/* Hero Section */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0f2027 0%, #203a43 50%, #2c5364 100%)',
        color: '#fff',
        py: 15,
        px: 2,
        textAlign: 'center'
      }}>
        <Container maxWidth="md">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <Typography 
              variant={isMobile ? "h3" : "h2"} 
              sx={{ 
                fontWeight: 800, 
                mb: 3,
                letterSpacing: '-0.5px'
              }}
            >
              Agency Solutions for the Creator Economy
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 5, 
                maxWidth: 700, 
                mx: 'auto',
                opacity: 0.9,
                lineHeight: 1.7
              }}
            >
              Powerful tools to manage creators, streamline payments, and accelerate collaborations
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate(routes.onboard)}
              sx={{
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 700,
                backgroundColor: theme.palette.primary.main,
                color: '#fff',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                '&:hover': { 
                  backgroundColor: theme.palette.primary.dark,
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 25px rgba(0, 0, 0, 0.2)'
                },
                transition: 'all 0.3s ease',
              }}
            >
              Request Access
            </Button>
          </motion.div>
        </Container>
      </Box>

      {/* Features Grid */}
      <Container maxWidth="lg" sx={{ py: 10, px: { xs: 2, md: 4 } }}>
        <Grid container spacing={6}>
          {sections.map((sec, idx) => (
            <Grid 
              item 
              xs={12} 
              md={6} 
              key={sec.id}
              ref={el => (refs.current[sec.id] = el)}
            >
              <motion.div
                custom={idx}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={cardVariants}
              >
                <Card sx={{ 
                  borderRadius: 4,
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  overflow: 'hidden',
                  height: '100%',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)'
                  }
                }}>
                  <Box sx={{ 
                    backgroundColor: sec.color, 
                    height: 8 
                  }} />
                  
                  <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                    <Chip 
                      label={sec.tag} 
                      size="small"
                      sx={{ 
                        mb: 2, 
                        backgroundColor: `${sec.color}22`,
                        color: sec.color,
                        fontWeight: 700,
                        fontSize: '0.8rem'
                      }}
                    />
                    
                    <Typography 
                      variant="h5" 
                      sx={{ 
                        fontWeight: 700,
                        letterSpacing: '-0.25px',
                        mb: 2
                      }}
                    >
                      {sec.title}
                    </Typography>
                    
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        lineHeight: 1.7, 
                        mb: 3,
                        color: theme.palette.text.secondary
                      }}
                    >
                      {sec.content}
                    </Typography>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: 3,
                      alignItems: 'center',
                      mt: 3
                    }}>
                      {/* Image Container */}
                      <Box sx={{ 
                        flex: 1, 
                        display: 'flex', 
                        justifyContent: 'center',
                        position: 'relative',
                        minHeight: 200
                      }}>
                        {sec.imageFrame && (
                          <Box sx={{ 
                            position: 'relative',
                            width: '100%',
                            maxWidth: 300
                          }}>
                            <Box 
                              component={motion.div}
                              whileHover={{ rotate: 1 }}
                              sx={{ 
                                position: 'relative', 
                                rotate: '3deg',
                                boxShadow: '0 12px 30px rgba(0,0,0,0.15)',
                                borderRadius: 2,
                                overflow: 'hidden',
                                width: '100%',
                                height: 'auto',
                                aspectRatio: '1/1'
                              }}
                            >
                              <img 
                                src={sec.frameImage} 
                                style={{ 
                                  position: 'absolute', 
                                  top: 0, 
                                  left: 0, 
                                  width: '100%', 
                                  height: '100%',
                                  objectFit: 'cover',
                                  zIndex: 2 
                                }} 
                              />
                              <img 
                                src={sec.previewImage} 
                                style={{ 
                                  position: 'absolute', 
                                  width: '88%', 
                                  height: '94%', 
                                  objectFit: 'cover', 
                                  left: '6%', 
                                  top: '3%', 
                                  borderRadius: 8, 
                                  zIndex: 1 
                                }} 
                              />
                            </Box>
                          </Box>
                        )}
                        
                        {sec.imageLaptop && (
                          <Box sx={{ 
                            position: 'relative',
                            width: '100%',
                            maxWidth: 350
                          }}>
                            <Box 
                              component={motion.div}
                              whileHover={{ y: -5 }}
                              sx={{ 
                                position: 'relative',
                                aspectRatio: '16/9',
                                width: '100%',
                                boxShadow: '0 15px 40px rgba(0,0,0,0.2)',
                                borderRadius: 1,
                                overflow: 'hidden'
                              }}
                            >
                              <img 
                                src={sec.frameImage} 
                                alt='Frame' 
                                style={{ 
                                  position: 'absolute', 
                                  top: 0, 
                                  left: 0, 
                                  width: '100%', 
                                  zIndex: 2 
                                }} 
                              />
                              <img 
                                src={sec.previewImage} 
                                style={{ 
                                  position: 'absolute', 
                                  width: '72%', 
                                  height: '84%', 
                                  objectFit: 'fill', 
                                  left: '14%', 
                                  top: '5.8%', 
                                  borderRadius: 4,
                                  zIndex: 1 
                                }} 
                              />
                            </Box>
                          </Box>
                        )}
                        
                        {sec.imageCircle && (
                          <Box 
                            component={motion.div}
                            whileHover={{ scale: 1.03 }}
                            sx={{
                              width: 180,
                              height: 180,
                              borderRadius: '50%',
                              overflow: 'hidden',
                              boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                              border: `4px solid ${sec.color}22`
                            }}
                          >
                            <CardMedia
                              component="img"
                              image={paymentImage}
                              alt="Payment"
                              sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          </Box>
                        )}
                        
                        {sec.imageSrc && !sec.imageFrame && !sec.imageLaptop && !sec.imageCircle && (
                          <Box 
                            component={motion.div}
                            whileHover={{ rotate: -1 }}
                            sx={{ 
                              width: '100%',
                              maxWidth: 280,
                              borderRadius: 3,
                              overflow: 'hidden',
                              boxShadow: '0 12px 30px rgba(0,0,0,0.15)'
                            }}
                          >
                            <CardMedia
                              component="img"
                              image={sec.imageSrc}
                              alt="Marketplace"
                              sx={{ width: '100%', height: 'auto' }}
                            />
                          </Box>
                        )}
                      </Box>
                      
                      {/* Button */}
                      <Box sx={{ flexShrink: 0 }}>
                        <Button
                          variant="contained"
                          onClick={() => navigate(routes.onboard)}
                          sx={{
                            px: 4,
                            py: 1.5,
                            fontWeight: 700,
                            backgroundColor: sec.color,
                            color: '#fff',
                            borderRadius: 2,
                            boxShadow: `0 4px 15px ${sec.color}40`,
                            '&:hover': { 
                              backgroundColor: sec.color,
                              transform: 'translateY(-3px)',
                              boxShadow: `0 6px 20px ${sec.color}60`
                            },
                            transition: 'all 0.3s ease',
                          }}
                        >
                          Request Access
                        </Button>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0f2027 0%, #203a43 100%)',
        color: '#fff',
        py: 10,
        textAlign: 'center'
      }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 3 }}>
            Ready to Transform Your Agency?
          </Typography>
          <Typography variant="h6" sx={{ mb: 5, maxWidth: 600, mx: 'auto', opacity: 0.9 }}>
            Join leading agencies already using Blitz to streamline creator management and payments
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate(routes.onboard)}
            sx={{
              px: 8,
              py: 2,
              fontSize: '1.1rem',
              fontWeight: 700,
              backgroundColor: theme.palette.primary.main,
              color: '#fff',
              borderRadius: 2,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
              '&:hover': { 
                backgroundColor: theme.palette.primary.dark,
                transform: 'translateY(-3px)',
                boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)'
              },
              transition: 'all 0.3s ease',
            }}
          >
            Request Demo
          </Button>
        </Container>
      </Box>

      <HomeFooter />
    </Box>
  );
}